/** ノードグラフ上の A*（コストは所要時間。docs/03_simulation.md 2 章） */
import {
  BIOME_HIKE_SPEED,
  HIKE_BASE_FACTOR,
  HIKE_DOWNHILL_FACTOR,
  HIKE_UPHILL_FACTOR,
} from '../../config/balance';
import { MinHeap } from '../../core/heap';
import { maxHikeKmh } from '../../core/hiking';
import type { EdgeData } from '../../core/world';
import type { Arc, Graph } from './graph';

export type EdgeCostFn = (edge: EdgeData, forward: boolean) => number;

export interface GraphPath {
  nodes: number[];
  arcs: Arc[];
  costS: number;
}

/** 歩行速度の上限（ヒューリスティック用。能力値の上限の補正も含める） */
const MAX_SPEED_MS =
  (maxHikeKmh(HIKE_UPHILL_FACTOR, HIKE_DOWNHILL_FACTOR) *
    HIKE_BASE_FACTOR *
    Math.max(...BIOME_HIKE_SPEED) *
    1.2) /
  3.6;

export function findPath(graph: Graph, from: number, to: number, cost: EdgeCostFn): GraphPath | null {
  if (from === to) return { nodes: [from], arcs: [], costS: 0 };
  const n = graph.nodes.length;
  const g = new Float64Array(n).fill(Infinity);
  const prev: (Arc | null)[] = new Array(n).fill(null);
  const prevNode = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);
  const target = graph.nodes[to];
  if (!target) return null;
  const h = (id: number): number => {
    const p = graph.nodes[id]?.pos;
    return p ? Math.hypot(p.x - target.pos.x, p.z - target.pos.z) / MAX_SPEED_MS : 0;
  };

  const heap = new MinHeap();
  g[from] = 0;
  heap.push(from, h(from));
  while (heap.size > 0) {
    const u = heap.pop();
    if (closed[u]) continue;
    closed[u] = 1;
    if (u === to) break;
    for (const arc of graph.arcs[u] ?? []) {
      if (closed[arc.to]) continue;
      const edge = graph.edges[arc.edgeId];
      if (!edge) continue;
      const w = cost(edge, arc.forward);
      if (!Number.isFinite(w)) continue;
      const ng = (g[u] as number) + w;
      if (ng < (g[arc.to] as number)) {
        g[arc.to] = ng;
        prev[arc.to] = arc;
        prevNode[arc.to] = u;
        heap.push(arc.to, ng + h(arc.to));
      }
    }
  }
  if (!Number.isFinite(g[to] as number)) return null;

  const nodes: number[] = [to];
  const arcs: Arc[] = [];
  for (let v = to; v !== from; v = prevNode[v] as number) {
    arcs.push(prev[v] as Arc);
    nodes.push(prevNode[v] as number);
  }
  return { nodes: nodes.reverse(), arcs: arcs.reverse(), costS: g[to] as number };
}

/**
 * 1 つのノードから全ノードへの所要時間（ダイクストラ法）。
 * toSource=true なら逆に「全ノードから source へ」の所要時間（弧の向きを逆にして評価する）。
 */
export function shortestTimes(
  graph: Graph,
  source: number,
  cost: EdgeCostFn,
  toSource = false,
): Float64Array {
  const n = graph.nodes.length;
  const dist = new Float64Array(n).fill(Infinity);
  const heap = new MinHeap();
  dist[source] = 0;
  heap.push(source, 0);
  while (heap.size > 0) {
    const u = heap.pop();
    const du = dist[u] as number;
    for (const arc of graph.arcs[u] ?? []) {
      const edge = graph.edges[arc.edgeId];
      if (!edge) continue;
      // toSource のときは arc.to → u の向きに歩く時間
      const w = cost(edge, toSource ? !arc.forward : arc.forward);
      if (!Number.isFinite(w)) continue;
      const nd = du + w;
      if (nd < (dist[arc.to] as number)) {
        dist[arc.to] = nd;
        heap.push(arc.to, nd);
      }
    }
  }
  return dist;
}
